import React, { useState, useEffect } from 'react';
import { X, Download, AlertTriangle, Edit3, Loader2, Sparkles, Target, Users, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoogleAdsPreviewModal = ({ isOpen, onClose, locationId, campaignParams, selectedCampaign }) => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (selectedCampaign) {
        // Load existing campaign data
        setPreviewData(selectedCampaign);
        setActiveTab('overview');
      } else if (locationId && campaignParams) {
        // Generate new campaign
        generatePreview();
      }
    }
  }, [isOpen, locationId, campaignParams, selectedCampaign]);

  const generatePreview = async () => {
    setLoading(true);
    setError(null);
    setPreviewData(null);

    try {
      const response = await axios.post(`${API}/google-ads/preview/${locationId}`, campaignParams);
      setPreviewData(response.data);
      
      // Auto-save is handled by the backend now - campaign is saved with auto-generated ID
      if (response.data.campaign_id) {
        console.log('Campaign auto-saved with ID:', response.data.campaign_id);
      }
      
    } catch (err) {
      console.error('Preview generation error:', err);
      
      // Ensure we always set a string error message, never an object
      let errorMessage = 'Failed to generate campaign preview';
      
      try {
        const errorDetail = err.response?.data?.detail || '';
        
        if (errorDetail.includes('No Company token available') || errorDetail.includes('company')) {
          errorMessage = 'Company-level authentication is required for the Google Ads Generator. Please re-authenticate with company access and try again.';
        } else if (err.response?.data?.detail) {
          errorMessage = String(err.response.data.detail);
        } else if (err.response?.data?.message) {
          errorMessage = String(err.response.data.message);
        } else if (err.message) {
          errorMessage = String(err.message);
        }
      } catch (parseError) {
        console.error('Error parsing error message:', parseError);
        errorMessage = 'An unexpected error occurred while generating the campaign';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const autoSave = async (updatedData) => {
    // Only auto-save if this is an existing campaign (has an ID)
    if (!updatedData.campaign_id && !previewData?.id) return;
    
    try {
      setAutoSaving(true);
      const campaignId = updatedData.campaign_id || previewData.id;
      
      await axios.put(`${API}/google-ads/campaigns/${locationId}/${campaignId}`, updatedData);
      setHasUnsavedChanges(false);
      console.log('Campaign auto-saved successfully');
      
    } catch (err) {
      console.error('Auto-save failed:', err);
      // Don't show error to user for auto-save failures
    } finally {
      setAutoSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!previewData) return;

    setDownloading(true);
    try {
      const response = await axios.post(`${API}/google-ads/download/${locationId}`, previewData, {
        responseType: 'blob'
      });

      // Create download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(previewData.studio_info.studio_name || previewData.studio_info.name || 'Studio').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-')}-Google-Ads-Campaign.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      onClose();
    } catch (err) {
      console.error('Download failed:', err);
      
      // Ensure we always set a string error message, never an object
      let errorMessage = 'Download failed';
      
      try {
        if (err.response?.data?.detail) {
          errorMessage = String(err.response.data.detail);
        } else if (err.response?.data?.message) {
          errorMessage = String(err.response.data.message);
        } else if (err.message) {
          errorMessage = String(err.message);
        }
      } catch (parseError) {
        console.error('Error parsing download error message:', parseError);
        errorMessage = 'An unexpected error occurred during download';
      }
      
      setError(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  const updateAdGroup = (index, field, value) => {
    const updated = { ...previewData };
    updated.ad_groups[index][field] = value;
    setPreviewData(updated);
    setHasUnsavedChanges(true);
    
    // Trigger auto-save after a short delay
    setTimeout(() => autoSave(updated), 1000);
  };

  const updateCampaignSetting = (field, value) => {
    const updated = { ...previewData };
    updated.campaign_settings[field] = value;
    setPreviewData(updated);
    setHasUnsavedChanges(true);
    
    // Trigger auto-save after a short delay
    setTimeout(() => autoSave(updated), 1000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b bg-white">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Google Ads Campaign Preview</h2>
              <p className="text-sm text-gray-600">
                {previewData ? `${previewData.studio_info.name} • ${previewData.studio_info.style}` : 'Generating...'}
                {autoSaving && <span className="text-blue-600 ml-2">💾 Auto-saving...</span>}
                {hasUnsavedChanges && !autoSaving && <span className="text-amber-600 ml-2">⚠️ Unsaved changes</span>}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {previewData && (
              <button
                onClick={() => setEditMode(!editMode)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                  editMode 
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Edit3 className="h-4 w-4" />
                <span>{editMode ? 'View Mode' : 'Edit Mode'}</span>
              </button>
            )}
            
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content - This now takes remaining height and scrolls */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Loading State */}
          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="relative">
                  <Loader2 className="h-16 w-16 text-blue-500 animate-spin mx-auto" />
                  <Sparkles className="h-6 w-6 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-gray-900">Generating Your Campaign</h3>
                  <p className="text-gray-600 max-w-md">
                    AI is analyzing your business and creating themed ad groups with targeted keywords...
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 max-w-md">
                  <p className="text-sm text-blue-700">
                    This may take 30-60 seconds while AI creates the perfect campaign structure for your business.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4 max-w-md">
                <div className="p-4 bg-red-50 rounded-full mx-auto w-fit">
                  <AlertTriangle className="h-12 w-12 text-red-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-gray-900">Campaign Generation Failed</h3>
                  <p className="text-gray-600">{error}</p>
                </div>
                <button
                  onClick={generatePreview}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Preview Content */}
          {previewData && (
            <>
              {/* Sidebar Navigation */}
              <div className="w-64 bg-gray-50 border-r overflow-y-auto">
                <div className="p-4 space-y-2">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      activeTab === 'overview' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium">Campaign Overview</div>
                    <div className="text-sm text-gray-500">Settings & Summary</div>
                  </button>
                  
                  {previewData.ad_groups.map((group, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTab(`group-${index}`)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        activeTab === `group-${index}` ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="font-medium truncate">{group.name}</div>
                      <div className="text-sm text-gray-500">{group.keywords.length} keywords</div>
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setActiveTab('extensions')}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      activeTab === 'extensions' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium">Extensions & Callouts</div>
                    <div className="text-sm text-gray-500">Sitelinks & Features</div>
                  </button>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 'overview' && (
                  <OverviewTab 
                    data={previewData} 
                    editMode={editMode} 
                    onUpdate={updateCampaignSetting}
                    onDownload={handleDownload}
                    downloading={downloading}
                  />
                )}
                
                {activeTab.startsWith('group-') && (
                  <AdGroupTab 
                    group={previewData.ad_groups[parseInt(activeTab.split('-')[1])]} 
                    index={parseInt(activeTab.split('-')[1])}
                    editMode={editMode} 
                    onUpdate={updateAdGroup}
                  />
                )}
                
                {activeTab === 'extensions' && (
                  <ExtensionsTab 
                    data={previewData} 
                    editMode={editMode} 
                    onUpdate={setPreviewData}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {previewData && (
          <div className="flex-shrink-0 border-t p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {editMode && (
                  <div className="flex items-center space-x-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      These elements are optimized for maximum performance. Edit with extreme caution.
                    </span>
                  </div>
                )}
              </div>
              
              <div className="text-sm text-gray-500">
                Switch to Campaign Overview tab to download your campaign file
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ data, editMode, onUpdate, onDownload, downloading }) => (
  <div className="p-6 space-y-6">
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Campaign Summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center p-4 bg-white rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{data.ad_groups.length}</div>
          <div className="text-sm text-gray-600">Ad Groups</div>
        </div>
        <div className="text-center p-4 bg-white rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {data.ad_groups.reduce((total, group) => total + group.keywords.length, 0)}
          </div>
          <div className="text-sm text-gray-600">Total Keywords</div>
        </div>
        <div className="text-center p-4 bg-white rounded-lg">
          <div className="text-2xl font-bold text-purple-600">
            {data.ad_groups.reduce((total, group) => total + group.headlines.length, 0)}
          </div>
          <div className="text-sm text-gray-600">Total Headlines</div>
        </div>
      </div>
    </div>

    {/* Campaign Settings */}
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h4 className="text-lg font-semibold mb-4">Campaign Settings</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Daily Budget</label>
          {editMode ? (
            <input
              type="number"
              value={data.campaign_settings.budget}
              onChange={(e) => onUpdate('budget', parseFloat(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          ) : (
            <div className="p-2 bg-gray-50 rounded-lg">${data.campaign_settings.budget}/day</div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Targeting Radius</label>
          {editMode ? (
            <input
              type="number"
              value={data.campaign_settings.targeting_radius}
              onChange={(e) => onUpdate('targeting_radius', parseFloat(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          ) : (
            <div className="p-2 bg-gray-50 rounded-lg">{data.campaign_settings.targeting_radius} miles</div>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Final URL</label>
          {editMode ? (
            <input
              type="url"
              value={data.campaign_settings.final_url}
              onChange={(e) => onUpdate('final_url', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          ) : (
            <div className="p-2 bg-gray-50 rounded-lg">{data.campaign_settings.final_url}</div>
          )}
        </div>
      </div>
    </div>

    {/* AI Theme Info */}
    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6">
      <div className="flex items-start space-x-3">
        <Sparkles className="h-6 w-6 text-purple-600 mt-1" />
        <div>
          <h4 className="text-lg font-semibold text-purple-900 mb-2">AI-Generated Campaign Strategy</h4>
          <p className="text-purple-700">{data.ai_theme_info}</p>
        </div>
      </div>
    </div>

    {/* Download Button */}
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-lg font-semibold text-gray-900">Ready to Launch</h4>
          <p className="text-sm text-gray-600 mt-1">
            Download your complete Google Ads campaign file for import into Google Ads Editor
          </p>
        </div>
        <button
          onClick={onDownload}
          disabled={downloading}
          className="flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 disabled:from-green-400 disabled:to-green-400 transition-all transform hover:scale-105 shadow-lg"
        >
          {downloading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="font-semibold">Generating File...</span>
            </>
          ) : (
            <>
              <Download className="h-5 w-5" />
              <span className="font-semibold">Download Campaign File</span>
            </>
          )}
        </button>
      </div>
    </div>
  </div>
);

// Ad Group Tab Component  
const AdGroupTab = ({ group, index, editMode, onUpdate }) => (
  <div className="p-6 space-y-6">
    {/* Ad Group Header */}
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900">
          {editMode ? (
            <input
              value={group.name}
              onChange={(e) => onUpdate(index, 'name', e.target.value)}
              className="text-xl font-bold bg-transparent border-b-2 border-blue-300 focus:border-blue-500 outline-none"
            />
          ) : (
            group.name
          )}
        </h3>
        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
          {group.ad_strength} Quality
        </span>
      </div>
      {group.theme && (
        <p className="text-gray-600 mb-4">{group.theme}</p>
      )}
    </div>

    {/* Keywords */}
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h4 className="text-lg font-semibold mb-4 flex items-center">
        <Search className="h-5 w-5 mr-2 text-gray-500" />
        Keywords ({group.keywords.length})
      </h4>
      <div className="flex flex-wrap gap-2">
        {group.keywords.map((keyword, i) => (
          <div key={i} className="flex items-center bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            {editMode ? (
              <input
                value={keyword}
                onChange={(e) => {
                  const newKeywords = [...group.keywords];
                  newKeywords[i] = e.target.value;
                  onUpdate(index, 'keywords', newKeywords);
                }}
                className="bg-transparent outline-none text-sm"
              />
            ) : (
              <span className="text-green-700 text-sm font-medium">{keyword}</span>
            )}
          </div>
        ))}
      </div>
    </div>

    {/* Headlines */}
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h4 className="text-lg font-semibold mb-4">Headlines ({group.headlines.length})</h4>
      <div className="grid grid-cols-1 gap-3 max-h-80 overflow-y-auto">
        {group.headlines.map((headline, i) => (
          <div key={i} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            {editMode ? (
              <input
                value={headline}
                onChange={(e) => {
                  const newHeadlines = [...group.headlines];
                  newHeadlines[i] = e.target.value;
                  onUpdate(index, 'headlines', newHeadlines);
                }}
                className="flex-1 bg-transparent outline-none font-medium"
              />
            ) : (
              <span className="font-medium text-blue-900">{headline}</span>
            )}
            <span className="text-xs text-blue-600 ml-2">
              {headline.length}/30
            </span>
          </div>
        ))}
      </div>
    </div>

    {/* Descriptions */}
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h4 className="text-lg font-semibold mb-4">Descriptions ({group.descriptions.length})</h4>
      <div className="space-y-3">
        {group.descriptions.map((description, i) => (
          <div key={i} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
            {editMode ? (
              <textarea
                value={description}
                onChange={(e) => {
                  const newDescriptions = [...group.descriptions];
                  newDescriptions[i] = e.target.value;
                  onUpdate(index, 'descriptions', newDescriptions);
                }}
                rows="2"
                className="w-full bg-transparent outline-none resize-none"
              />
            ) : (
              <p className="text-purple-900">{description}</p>
            )}
            <div className="text-xs text-purple-600 mt-1">
              {description.length}/90
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Extensions Tab Component
const ExtensionsTab = ({ data, editMode, onUpdate }) => (
  <div className="p-6 space-y-6">
    {/* Sitelinks */}
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h4 className="text-lg font-semibold mb-4">Sitelinks ({data.sitelinks.length})</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.sitelinks.map((sitelink, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-4">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Link Text</label>
                {editMode ? (
                  <input
                    value={sitelink.link_text}
                    onChange={(e) => {
                      const newSitelinks = [...data.sitelinks];
                      newSitelinks[i].link_text = e.target.value;
                      onUpdate({ ...data, sitelinks: newSitelinks });
                    }}
                    className="w-full p-2 text-sm border border-gray-300 rounded"
                  />
                ) : (
                  <div className="font-medium text-blue-600">{sitelink.link_text}</div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description 1</label>
                {editMode ? (
                  <input
                    value={sitelink.description_line1}
                    onChange={(e) => {
                      const newSitelinks = [...data.sitelinks];
                      newSitelinks[i].description_line1 = e.target.value;
                      onUpdate({ ...data, sitelinks: newSitelinks });
                    }}
                    className="w-full p-2 text-sm border border-gray-300 rounded"
                  />
                ) : (
                  <div className="text-sm text-gray-600">{sitelink.description_line1}</div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description 2</label>
                {editMode ? (
                  <input
                    value={sitelink.description_line2}
                    onChange={(e) => {
                      const newSitelinks = [...data.sitelinks];
                      newSitelinks[i].description_line2 = e.target.value;
                      onUpdate({ ...data, sitelinks: newSitelinks });
                    }}
                    className="w-full p-2 text-sm border border-gray-300 rounded"
                  />
                ) : (
                  <div className="text-sm text-gray-600">{sitelink.description_line2}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Callouts */}
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h4 className="text-lg font-semibold mb-4">Callouts ({data.callouts.length})</h4>
      <div className="flex flex-wrap gap-2">
        {data.callouts.map((callout, i) => (
          <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {editMode ? (
              <input
                value={callout}
                onChange={(e) => {
                  const newCallouts = [...data.callouts];
                  newCallouts[i] = e.target.value;
                  onUpdate({ ...data, callouts: newCallouts });
                }}
                className="bg-transparent outline-none text-sm font-medium text-amber-700"
              />
            ) : (
              <span className="text-amber-700 text-sm font-medium">{callout}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default GoogleAdsPreviewModal;